import React, { useEffect, useRef, useState } from "react";
import { useProjects } from "../context/ProjectContext";
import api from "../lib/api";
import { exportDashboardPdf } from "../lib/dashboardPdf";
import { toast } from "sonner";
import KpiCard from "../components/KpiCard";
import { NoProject, EmptyState } from "../components/EmptyState";
import { nf, pf, signed, money } from "../lib/format";
import { pfColor, gainColor } from "../lib/status";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import {
  Clock, Activity, TrendingUp, Gauge, FileDown, AlertTriangle, Loader2,
  CheckCircle2, Eye, XCircle, Timer,
} from "lucide-react";

const StatusCount = ({ label, count, color, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-3 text-center" data-testid={testid}>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{count}</div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
  </div>
);

export default function Dashboard() {
  const { selected } = useProjects();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!selected) { setLoading(false); return; }
    setLoading(true);
    api.get(`/projects/${selected.id}/dashboard`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [selected]);

  if (!selected) return <NoProject />;
  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;
  if (!data) return <EmptyState title="No data yet" hint="Add cost codes and weekly actuals to see results." />;

  const t = data.totals;
  const c = t.status_counts;

  const handleExportPdf = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      await exportDashboardPdf(contentRef.current, {
        projectName: selected.project_name,
        projectNumber: selected.project_number,
        reportingDate: selected.reporting_date,
      });
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the dashboard PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-slate-900">{selected.project_name}</h1>
          <p className="text-sm text-slate-500">
            #{selected.project_number} · Reporting date {selected.reporting_date || "—"}
          </p>
        </div>
        <Button onClick={handleExportPdf} disabled={exporting} data-testid="export-dashboard-pdf"
                className="bg-[#E8791A] hover:bg-[#D66A0F] text-white">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          {exporting ? "Generating…" : "Dashboard PDF"}
        </Button>
      </div>

      <div ref={contentRef} className="space-y-5">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="kpi-revised-hours" label="Revised Budget Hours" icon={Clock} value={nf(t.total_revised_hours)} />
        <KpiCard testid="kpi-actual-hours" label="Actual Hours" icon={Activity} value={nf(t.total_actual_hours)}
                 sub={`ST ${nf(t.total_st_hours)} · OT ${nf(t.total_ot_hours)}`} />
        <KpiCard testid="kpi-earned-hours" label="Earned Hours" icon={TrendingUp} value={nf(t.total_earned_hours)} />
        <KpiCard testid="kpi-productivity-factor" label="Overall Productivity Factor" icon={Gauge}
                 value={pf(t.overall_productivity_factor)} accent={pfColor(t.overall_productivity_factor)}
                 sub={t.overall_productivity_factor >= 1 ? "Favorable" : "Unfavorable"} />
        <KpiCard testid="kpi-forecast-hours" label="Forecast Hours @ Completion" icon={Timer} value={nf(t.total_forecast_hours)} />
        <KpiCard testid="kpi-forecast-gain" label="Forecast Hours Gain/(Loss)" value={signed(t.forecast_gain_loss)}
                 accent={gainColor(t.forecast_gain_loss)} />
        <KpiCard testid="kpi-jtd-hours" label="JTD Hours Gain/(Loss)" value={signed(t.jtd_hours_gain_loss)}
                 accent={gainColor(t.jtd_hours_gain_loss)} />
        <KpiCard testid="kpi-jtd-dollars" label="JTD Dollar Gain/(Loss)" value={money(t.jtd_dollar_gain_loss)}
                 accent={gainColor(t.jtd_dollar_gain_loss)} />
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatusCount testid="status-on-track" label="On Track" count={c.on_track} color="#047857" />
        <StatusCount testid="status-watch" label="On Watch" count={c.watch} color="#B45309" />
        <StatusCount testid="status-unfavorable" label="Unfavorable" count={c.unfavorable} color="#B91C1C" />
        <StatusCount testid="status-review" label="Needs Review" count={c.review} color="#1D4ED8" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Earned vs Actual Hours by Cost Code" testid="chart-earned-actual">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_code} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="cost_code" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="earned_hours" name="Earned" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual_hours" name="Actual" fill="#0B192C" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revised vs Forecast Hours by Cost Code" testid="chart-revised-forecast">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_code} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="cost_code" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="revised_hours" name="Revised" fill="#1E3E62" radius={[3, 3, 0, 0]} />
              <Bar dataKey="forecast_hours" name="Forecast" fill="#E8791A" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weekly Straight-Time vs Overtime" testid="chart-weekly-hours">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.weekly} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week_ending" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="st" name="Straight-Time" stackId="h" fill="#1E3E62" />
              <Bar dataKey="ot" name="Overtime" stackId="h" fill="#E8791A" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weekly Productivity-Factor Trend" testid="chart-weekly-pf">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.weekly} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week_ending" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
              <Tooltip />
              <Line type="monotone" dataKey="pf" name="PF" stroke="#E8791A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top movers */}
      <div className="grid lg:grid-cols-2 gap-4">
        <TopList title="Top 5 Favorable Cost Codes" rows={data.top_favorable} positive testid="top-favorable" />
        <TopList title="Top 5 Unfavorable Cost Codes" rows={data.top_unfavorable} testid="top-unfavorable" />
      </div>
      </div>
    </div>
  );
}

const ChartCard = ({ title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm" data-testid={testid}>
    <h3 className="font-head text-base font-semibold text-slate-800 mb-3">{title}</h3>
    {children}
  </div>
);

const TopList = ({ title, rows, positive, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm" data-testid={testid}>
    <h3 className="font-head text-base font-semibold text-slate-800 mb-3">{title}</h3>
    {(!rows || rows.length === 0) ? (
      <div className="text-sm text-slate-400 py-4 text-center">No data</div>
    ) : (
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.cost_code} className="flex items-center justify-between py-2 text-sm">
            <div>
              <span className="font-mono font-medium">{r.cost_code}</span>
              <span className="text-slate-500 ml-2">{r.description}</span>
            </div>
            <span className={`font-mono font-semibold ${positive ? "text-emerald-700" : "text-red-700"}`}>
              {signed(r.jtd_hours_gain_loss)} hrs
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
